/* globals document, window */
// @flow
import React, { Component } from "react";
import { GlobalProvider } from "../common/GlobalContext";
import { ListingProvider } from "./ListingContext";
import GlobalAlert from "../common/GlobalAlert";
import PdpNavigation from "./PdpNavigation";
import Hero from "./Hero";
import Main from "./Main";
import ListingGrid from "../common/ListingGrid";
import LeafletMap from "../common/LeafletMap";
import Disclaimer from "./Disclaimer";
import DisclaimerSold from "./DisclaimerSold";
import OnboardNavigator from "../common/OnboardNavigator";
import Ads from "../common/Ads";
import request from "request";
import getUuid from "uuid-by-string";
import * as _ from "lodash";

import type {
  CompliantListingType,
  $ListingResponse,
  $OpenHouses,
  ComplianceListingItems,
  $AdditionalDetailsLayout,
  $AdditionalFields,
} from "flow/types";

type $Props = {
  alerts: Object,
  areaSales: $ListingResponse,
  audiostreamKey: string | null,
  openHouses: $OpenHouses,
  compliance: ComplianceListingItems,
  favorites: Array<number>,
  RECAPTCHA_CLIENT: string,
  RECAPTCHA_CLIENT_V2: string,
  similar: $ListingResponse,
  siteUser: Object,
  theme: Object, // theme from the API/site settings, not the react-jss theme
  officeName: string,
  mapProvider: Object,
  CDN_IMAGE_SERVER_URL: string,
  previousFormFieldValues: Object,
  showAds: boolean,
  openIndexes?: Array<number>,
  mlsApiVersion: number,
  listing: CompliantListingType,
  additionalDetailsLayout: $AdditionalDetailsLayout,
  additionalFields: $AdditionalFields,
  ip: string | null,
  aiSummary: string | null,
  isAiEnabled: boolean,
};

const listTracLogEventView = ({ mlsListingId, zipCode }) => {
  if (window._LT) {
    window._LT._trackEvent(window._eventType.view, mlsListingId, zipCode);
  }
};

class ListingDetail extends Component<$Props> {
  componentDidMount() {
    const { listing, mlsApiVersion, audiostreamKey, theme, ip } = this.props;

    // Track view Lisitngs for CREA
    if (_.get(listing, "datastoreProviderId") === 233) {
      const nav = window.navigator;
      const screen = window.screen;
      const destinationID = "48254";
      // Creating UUID based on userAgent/browser/device/ip address
      const uuid =
        getUuid(
          nav.mimeTypes.length +
            nav.userAgent +
            (screen.height || "") +
            ip +
            (screen.width || "") +
            (screen.pixelDepth || "")
        ) +
        "-" +
        destinationID;
      const url = "https://analytics.crea.ca/LogEvents.svc/LogEvents";
      const qs = {
        ListingID: _.get(listing, "mlsListingId"),
        DestinationID: destinationID,
        EventType: "view",
        UUID: uuid,
        IP: ip,
      };
      try {
        request.get({
          url,
          qs,
        });
      } catch (e) {
        //ignore errors
      }
    }

    if (
      mlsApiVersion === 1 &&
      listing.compliance &&
      listing.compliance.usesListTrac
    ) {
      const script = document.createElement("script");
      script.src =
        "//code.listtrac.com/monitor.ashx?acct=x_101105&callback=logview&nonjq=1";
      script.async = true;
      const callback = function() {
        // Log the Listing Viewed
        listTracLogEventView(listing);
      };
      script.onload = callback;
      // $FlowIgnore
      document.head.appendChild(script);
    }

    // if we are using audiostream render the script tag
    if (audiostreamKey) {
      const script = document.createElement("script");
      script.src = "/audiostream/js/vpPlayerBundle.min.js";
      script.async = true;

      const css = document.createElement("link");
      css.href = "/audiostream/css/theme.min.css";
      css.rel = "stylesheet";
      // $FlowIgnore
      document.head.appendChild(script);
      // $FlowIgnore
      document.head.appendChild(css);

      const inlineCss = `.vp-player .vp-icon-volume-up:before { color: ${
        theme.primaryColor
      } !important; }`;
      const style = document.createElement("style");
      style.appendChild(document.createTextNode(inlineCss));
      // $FlowIgnore
      document.head.appendChild(style);

      const callback = function() {
        /* eslint-disable */
        // $FlowIgnore
        new vpStreaming.player("audiostream", {
          streamKey: `${audiostreamKey}`,
        });
        /* eslint-enable */

        document.getElementsByClassName("eng-play")[0].style.background =
          theme.primaryColor;
        document.getElementsByClassName("eng-play")[0].style.borderColor =
          theme.primaryColor;
        document.getElementsByClassName("eng-play")[0].style.color =
          theme.primaryFontColor;

        document.getElementsByClassName("esp-play")[0].style.background =
          theme.primaryColor;
        document.getElementsByClassName("esp-play")[0].style.borderColor =
          theme.primaryColor;
        document.getElementsByClassName("esp-play")[0].style.color =
          theme.primaryFontColor;

        document.getElementsByClassName("stop-ctr")[0].style.background =
          theme.primaryColor;
        document.getElementsByClassName("stop-ctr")[0].style.borderColor =
          theme.primaryColor;
        document.getElementsByClassName("stop-ctr")[0].style.color =
          theme.primaryFontColor;

        document.getElementsByClassName("loading-ctr")[0].style.background =
          theme.primaryColor;
        document.getElementsByClassName("loading-ctr")[0].style.borderColor =
          theme.primaryColor;
        document.getElementsByClassName("loading-ctr")[0].style.color =
          theme.primaryFontColor;
      };
      script.onload = callback;
    }
  }

  render() {
    const {
      alerts,
      areaSales,
      openHouses,
      compliance,
      favorites,
      listing,
      RECAPTCHA_CLIENT,
      RECAPTCHA_CLIENT_V2,
      similar,
      siteUser,
      theme,
      officeName,
      mapProvider,
      CDN_IMAGE_SERVER_URL,
      previousFormFieldValues,
      showAds,
      openIndexes,
      mlsApiVersion,
      additionalDetailsLayout,
      additionalFields,
      isAiEnabled,
      aiSummary,
    } = this.props;
    const isV1 = mlsApiVersion === 1;

    return (
      <GlobalProvider
        compliance={compliance}
        favorites={favorites}
        siteUser={siteUser}
        theme={theme}
        RECAPTCHA_CLIENT={RECAPTCHA_CLIENT}
        RECAPTCHA_CLIENT_V2={RECAPTCHA_CLIENT_V2}
        mapProvider={mapProvider}
        CDN_IMAGE_SERVER_URL={CDN_IMAGE_SERVER_URL}
        previousFormFieldValues={previousFormFieldValues}
      >
        <ListingProvider
          value={{
            areaSales,
            listing,
            similar,
            openIndexes,
            openHouses,
          }}
        >
          <GlobalAlert alerts={alerts} />
          <PdpNavigation />
          <Hero isV1={isV1} />
          <Main
            additionalDetailsLayout={additionalDetailsLayout}
            additionalFields={additionalFields}
            aiSummary={aiSummary}
            isAiEnabled={isAiEnabled}
          />
          <Disclaimer
            compliance={compliance}
            listing={listing}
            complianceKey="listingInformationDisclaimer"
          />
          <Disclaimer
            compliance={compliance}
            listing={listing}
            complianceKey="footerDisclaimer"
          />
          <LeafletMap
            property={listing}
            statusOverride={listing.status}
            defaultZoom={19}
            mapTypeId={mapProvider.tileLayers.satellite}
            scrollWheelZoom={false}
          />

          {similar && similar.listings && similar.listings.length > 0 ? (
            <ListingGrid
              title="Similar homes you may like"
              listings={similar && similar.listings}
              officeName={officeName}
              pdpAction="similarHomes"
            />
          ) : null}
          {areaSales && areaSales.listings && areaSales.listings.length > 0 ? (
            <ListingGrid
              title="Sold homes in your area"
              listings={
                areaSales &&
                areaSales.listings &&
                areaSales.listings.slice(0, 6)
              }
              officeName={officeName}
              pdpAction="soldHomes"
            />
          ) : null}
          {/* COMPLIANCE: Triad homes must show "sold" disclaimer– this includes "Area Homes" */}
          {areaSales &&
            areaSales.listings &&
            areaSales.listings.length > 0 && <DisclaimerSold />}
          {compliance.isOutsideUS ? null : (
            <OnboardNavigator
              filters={{
                zipCode: listing.zipCode,
                city: listing.city,
                state: listing.state,
              }}
            />
          )}
          <Ads showAds={showAds} position="footer" />
        </ListingProvider>
      </GlobalProvider>
    );
  }
}

export default ListingDetail;
